'use client';

import { useEffect, useRef, useState } from 'react';
import { Loader2 } from 'lucide-react';
import pageStyles from '@/src/app/[lang]/north-star-dashboard/page.module.scss';

export interface ReasoningMessage {
  author: 'strategist' | 'domain-expert' | 'product-lead' | 'data-lead';
  message: { fr: string; en: string };
}

interface ReasoningChatProps {
  messages: ReasoningMessage[];
  isEn: boolean;
  onComplete: () => void;
}

export function ReasoningChat({ messages, isEn, onComplete }: ReasoningChatProps) {
  const [visibleCount, setVisibleCount] = useState(0);
  const [typing, setTyping] = useState(true);
  const containerRef = useRef<HTMLDivElement>(null);

  const authorLabels: Record<
    ReasoningMessage['author'],
    { fr: string; en: string; initial: string; color: string }
  > = {
    strategist: { fr: 'Stratège', en: 'Strategist', initial: 'S', color: '#b98745' },
    'domain-expert': { fr: 'Expert métier', en: 'Domain expert', initial: 'E', color: '#0ea5e9' },
    'product-lead': { fr: 'Lead produit', en: 'Product lead', initial: 'P', color: '#15803d' },
    'data-lead': { fr: 'Lead data', en: 'Data lead', initial: 'D', color: '#7c3aed' },
  };

  useEffect(() => {
    if (visibleCount >= messages.length) {
      setTyping(false);
      const timer = setTimeout(onComplete, 1800);
      return () => clearTimeout(timer);
    }
    const delay = visibleCount === 0 ? 800 : 2200;
    const timer = setTimeout(() => {
      setVisibleCount((prev) => prev + 1);
    }, delay);
    return () => clearTimeout(timer);
  }, [visibleCount, messages.length, onComplete]);

  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.scrollTop = containerRef.current.scrollHeight;
    }
  }, [visibleCount]);

  const visibleMessages = messages.slice(Math.max(0, visibleCount - 4), visibleCount);

  return (
    <div className={pageStyles.reasoningChat} data-testid="reasoning-chat">
      <div className={pageStyles.reasoningChatHeader}>
        <div className={pageStyles.reasoningChatDot} />
        <span>{isEn ? 'Averiane reasoning' : 'Raisonnement Averiane'}</span>
      </div>
      <div className={pageStyles.reasoningChatWindow} ref={containerRef}>
        {visibleMessages.map((msg, idx) => {
          const author = authorLabels[msg.author];
          const isLatest = idx === visibleMessages.length - 1;
          return (
            <div
              key={visibleCount - visibleMessages.length + idx}
              className={`${pageStyles.reasoningMessage} ${isLatest ? pageStyles.reasoningMessageLatest : ''}`}
            >
              <div
                className={pageStyles.reasoningAvatar}
                style={{ background: `${author.color}18`, color: author.color }}
              >
                {author.initial}
              </div>
              <div className={pageStyles.reasoningBubble}>
                <div className={pageStyles.reasoningAuthor} style={{ color: author.color }}>
                  {isEn ? author.en : author.fr}
                </div>
                <div className={pageStyles.reasoningText}>
                  {isEn ? msg.message.en : msg.message.fr}
                </div>
              </div>
            </div>
          );
        })}
        {typing && (
          <div className={pageStyles.reasoningTyping}>
            <Loader2 size={14} className={pageStyles.graphLoaderSpin} />
            <div className={pageStyles.reasoningTypingDot} />
            <div className={pageStyles.reasoningTypingDot} />
            <div className={pageStyles.reasoningTypingDot} />
          </div>
        )}
      </div>
    </div>
  );
}

interface GraphLoaderProps {
  label: string;
  progress: number;
}

export function GraphLoader({ label, progress }: GraphLoaderProps) {
  return (
    <div className={pageStyles.graphLoader} data-testid="graph-loader">
      <div className={pageStyles.graphLoaderInner}>
        <Loader2 size={16} className={pageStyles.graphLoaderSpin} />
        <span>{label}</span>
        <span className={pageStyles.graphLoaderCount}>{Math.round(progress)}%</span>
      </div>
      <div className={pageStyles.graphLoaderBar}>
        <div className={pageStyles.graphLoaderFill} style={{ width: `${progress}%` }} />
      </div>
    </div>
  );
}
